import React, { useCallback, useEffect, useMemo, useState } from "react";
import FormFieldTwoColumns from "../../Layouts/FormFieldTwoColumns";
import { Autocomplete, AutocompleteProps, CircularProgress, InputAdornment, TextField } from "@mui/material";
import TranslationWrapper, { getTranslation } from "../../../TranslationWrapper/TranslationWrapper";
import { FieldError, useController, UseControllerProps } from "react-hook-form";
import _ from "lodash";
import { AsyncAutocompleteFetchAdapter } from "../AutocompleteRoot";
import { ProductForSelection } from "./AutcompleteProductTypes";
import { DefaultOptionForAsyncProductAutocomplete } from "./DefaultOptionForAsyncProductAutocomplete";
import { LegacyAutocompleteProductFetch } from "./LegacyAutocompleteProductFetch";
import { ControllerAutocompleteProductFetch } from "./ControllerAutocompleteProductFetch";

const PRODUCT_ID_NOT_FOUND = -1;

function getOptionLabel(option: ProductForSelection) {
  if (option.label != null && option.label.length > 0) {
    return option.label;
  }

  return option.naam ?? "Missing name/sysname";
}

export function AsyncProductAutocomplete({
  async_url,
  autocompleteProps,
  value,
  fetchAdapterVariant = "legacy",
  onChange,
  onBlur,
  error,
  disabled = false,
  rules,
  multiple = false,
}: {
  autocompleteProps?: AutocompleteProps<ProductForSelection, undefined, undefined, undefined>;
  async_url: string;
  fetchAdapterVariant?: "legacy" | "custom-controller";
  onChange: (newProduct: Array<ProductForSelection> | ProductForSelection | null) => void;
  onBlur?: () => void;
  error?: FieldError;
  rules?: UseControllerProps["rules"];
  disabled?: boolean;
} & (
  | { multiple: false; value?: ProductForSelection["value"] | null | undefined }
  | { multiple: true; value?: ProductForSelection["value"][] }
)) {
  // Workaround: in the db some product "empty" values are defined as "0" rather than as "null"
  const valueDatabaseFix = useMemo(() => {
    if (Array.isArray(value)) {
      return value.filter((val) => val != 0);
    } else if (value === 0) {
      return null;
    } else if (value == undefined) {
      return null;
    } else {
      return value;
    }
  }, [value]);

  const [open, setOpen] = useState(false);

  const [options, _setOptions] = useState<ProductForSelection[]>([]);
  const setOptions = useCallback((newOptions: ProductForSelection[]) => {
    return _setOptions((allOptions) => _.sortBy(_.unionBy(allOptions, newOptions, "value"), "value"));
  }, []);
  const [isLoadingInitial, setLoadingInitial] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState<Array<ProductForSelection> | ProductForSelection | null>(
    multiple ? [] : null
  );

  const fetchAdapter = useMemo<AsyncAutocompleteFetchAdapter<ProductForSelection>>(() => {
    if (fetchAdapterVariant === "legacy") {
      return new LegacyAutocompleteProductFetch(async_url);
    } else if (fetchAdapterVariant === "custom-controller") {
      return new ControllerAutocompleteProductFetch(async_url);
    } else {
      return new ControllerAutocompleteProductFetch(async_url);
    }
  }, [fetchAdapterVariant, async_url]);

  const loading = open && options.length === 0;

  // loads all options
  useEffect(() => {
    (async () => {
      const options = await fetchAdapter.search();
      console.log("FETCHING");
      console.log(options);
      setOptions(options);
    })();
  }, [async_url, fetchAdapter, setOptions]);

  useEffect(() => {
    if (valueDatabaseFix && options.length > 0) {
      console.log("SETTING VALUE");
      const optionsMap = _.keyBy(options, "value");

      if (Array.isArray(valueDatabaseFix)) {
        console.log(valueDatabaseFix.map((value) => optionsMap[value]).filter((a) => a));

        setSelectedProducts((selectedProducts) => {
          const map = _.keyBy(selectedProducts, "value");
          return valueDatabaseFix.map((value) => optionsMap[value] ?? map[value]).filter((a) => a);
        });
      } else {
        console.log(optionsMap[valueDatabaseFix] ?? null);
        setSelectedProducts(optionsMap[valueDatabaseFix] ?? null);
      }
    } else if (!valueDatabaseFix) {
      setSelectedProducts(multiple ? [] : null);
    }
  }, [multiple, options, valueDatabaseFix]);

  // Sorry voor deze hacks, het lukte mij echt niet.
  const selectedMemo = useMemo(() => {
    return JSON.stringify(selectedProducts);
  }, [selectedProducts]);

  const selectedMemo2 = useMemo(() => {
    return JSON.parse(selectedMemo);
  }, [selectedMemo]);
  // loads initial value from server
  useEffect(() => {
    console.log(valueDatabaseFix);
    if (isLoadingInitial) {
      (async () => {
        try {
          if (typeof valueDatabaseFix === "number" || valueDatabaseFix?.length) {
            const products = await fetchAdapter.fetchInitialValue(valueDatabaseFix);
            console.log("PRODUCT LOADED");
            if (products == null) {
              const v: ProductForSelection = {
                value: PRODUCT_ID_NOT_FOUND,
                label: `Product with id ${valueDatabaseFix} not found`,
                blocked: false,
                is_actief: true,
                prijs: 0,
                id: PRODUCT_ID_NOT_FOUND,
                naam: "",
                snaam: "",
              };
              setSelectedProducts(multiple ? [v] : v);
              setOptions([v]);
            } else {
              setSelectedProducts(products);
              setOptions(_.isArray(products) ? products : [products]);
            }
          }
        } finally {
          console.log("LOADING FALSE");
          setLoadingInitial(false);
        }
      })();
    }
  }, [valueDatabaseFix, isLoadingInitial, fetchAdapter, multiple, setOptions]);

  return isLoadingInitial ? (
    <CircularProgress />
  ) : (
    <Autocomplete<ProductForSelection, typeof multiple>
      disableCloseOnSelect={multiple}
      disabled={disabled}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(v1, v2) => {
        return v1.value === v2.value;
      }}
      loading={loading}
      multiple={multiple}
      onBlur={() => {
        onBlur?.();
      }}
      onChange={(_, option) => {
        onChange(option);
      }}
      onClose={() => {
        setOpen(false);
      }}
      onInputChange={async (__, value) => {
        if (value.length) {
          setOptions(await fetchAdapter.search(value));
        }
      }}
      onOpen={() => {
        setOpen(true);
      }}
      options={options}
      renderInput={(params) => {
        let product_not_found = false;
        let product_inactive_or_blocked = false;

        if (selectedProducts) {
          if (_.isArray(selectedProducts)) {
            product_not_found = selectedProducts.some((v) => v?.value === PRODUCT_ID_NOT_FOUND);
            product_inactive_or_blocked = selectedProducts.some(
              (v) => (Number.isFinite(v.value) && !v.is_actief) || v.blocked
            );
          } else {
            product_not_found = selectedProducts.value === PRODUCT_ID_NOT_FOUND;
            product_inactive_or_blocked =
              (Number.isFinite(selectedProducts.value) && !selectedProducts.is_actief) || selectedProducts.blocked;
          }
        }

        return (
          <TextField
            {...params}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position={"end"}>
                  {loading ? <CircularProgress color={"inherit"} size={20} /> : null}
                  {params.InputProps.endAdornment}
                </InputAdornment>
              ),
            }}
            error={Boolean(error) || product_not_found || product_inactive_or_blocked}
            helperText={
              error
                ? error.message
                : product_not_found
                  ? getTranslation("ui.single-product-select.err.product_not_found", "Product cannot be found")
                  : product_inactive_or_blocked
                    ? getTranslation(
                        "ui.single-product-select.err.inactive_or_blocked",
                        "Product is inactive or blocked"
                      )
                    : undefined
            }
            label={"Select a product"}
            required={Boolean(rules?.required)}
            variant={"outlined"}
          />
        );
      }}
      renderOption={(props, option) => {
        option.label = getOptionLabel(option);
        return (
          <li {...props} key={option.value}>
            {/* TODO Render arrangement link if arrangement_id exists, using some sort of extension to this component */}
            <DefaultOptionForAsyncProductAutocomplete option={option} />
          </li>
        );
      }}
      size={"small"}
      style={{ width: "100%", backgroundColor: "rgba(100,100,100,0.05)" }}
      value={selectedMemo2}
      {...autocompleteProps}
    />
  );
}

/**
 * A specialized version of <FormAsyncTC/> for products only.
 * You can override any <Autocomplete/> prop defaults by passing props.autocompleteProps
 * @see FormAsyncTC
 *
 * @param async_url
 * @param name
 * @param should_show_link Turn off if you don't want to show a link to the product page in the label
 * @param rules
 * @param defaultValue
 * @param tKey
 * @param tDefault
 * @param formFieldProps
 * @param autocompleteProps
 * @constructor
 */
function AsyncProductAutocompleteTC({
  async_url,
  name,
  should_show_link,
  rules,
  defaultValue,
  tKey,
  tDefault,
  formFieldProps,
  autocompleteProps,
}: {
  autocompleteProps?: AutocompleteProps<ProductForSelection, undefined, undefined, undefined>;
  formFieldProps?: any;
  async_url: string;
  name: string;
  should_show_link: boolean;
} & UseControllerProps & {
    tKey: string;
    tDefault: string;
  }) {
  const {
    field: { value: product_id, onChange, onBlur },
    fieldState: { error },
  } = useController({ name, rules, defaultValue });

  return (
    <FormFieldTwoColumns
      label={
        <>
          <TranslationWrapper tDefault={tDefault} tKey={tKey} />
          {product_id && should_show_link ? (
            <a
              href={`/manager/producten/${product_id}/edit`}
              rel={"noreferrer"}
              target={"_blank"}
              title={"Edit selected product"}
            >
              {" ("}
              <TranslationWrapper tDefault={"link"} tKey={"product_autocomplete.link_to_product"} />)
            </a>
          ) : null}
        </>
      }
      {...formFieldProps}
    >
      <AsyncProductAutocomplete
        async_url={async_url}
        autocompleteProps={autocompleteProps}
        error={error}
        onBlur={onBlur}
        onChange={(option) => {
          if (_.isArray(option)) {
            onChange({ target: { value: option } }, option);
          } else {
            onChange({ target: { value: option?.value ?? null } }, option?.value ?? null);
          }
        }}
        rules={rules}
        value={product_id}
      />
    </FormFieldTwoColumns>
  );
}

AsyncProductAutocompleteTC.defaultProps = {
  should_show_link: true,
};

export default AsyncProductAutocompleteTC;
